import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

interface StudentExpiringPaymentMethod {
  studentId: string;
  studentName: string;
  paymentMethodId: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  expirationDate: string;
  daysUntilExpiration: number;
  isExpired: boolean;
}

interface StudentPaymentExpirationBannerProps {
  onClose?: () => void;
}

export default function StudentPaymentExpirationBanner({ onClose }: StudentPaymentExpirationBannerProps) {
  const { getAuthToken } = useAuth();
  const [expiringMethods, setExpiringMethods] = useState<StudentExpiringPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkExpiringStudentPaymentMethods = async () => {
      try {
        const authToken = getAuthToken();
        
        if (!authToken) {
          setLoading(false);
          return;
        }

        // REAL API CALL
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/stripe/check-expiring-student-payment-methods`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        // console.log("StudentPaymentExpirationBanner: Response status:", response.status);

        if (!response.ok) {
          throw new Error("Failed to check expiring student payment methods");
        }

        const data = await response.json();
        // console.log("StudentPaymentExpirationBanner: Response data:", JSON.stringify(data, null, 2));
        
        setExpiringMethods(data.expiringMethods || []);
        
        // Only show banner if there are expiring methods
        if (data.expiringMethods && data.expiringMethods.length > 0) {
          // console.log("StudentPaymentExpirationBanner: Found expiring student methods, showing banner");
          setIsVisible(true);
        } else {
          // console.log("StudentPaymentExpirationBanner: No expiring student methods found");
        }
      } catch (err) {
        // console.error("StudentPaymentExpirationBanner: Error:", err);
        // Silently handle errors
      } finally {
        setLoading(false);
      }
    };

    checkExpiringStudentPaymentMethods();
  }, [getAuthToken]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (loading || !isVisible || expiringMethods.length === 0) {
    return null;
  }

  const urgentCount = expiringMethods.filter(m => m.daysUntilExpiration <= 14).length;
  const warningCount = expiringMethods.filter(m => m.daysUntilExpiration > 14).length;

  const getUrgencyColor = () => {
    if (urgentCount > 0) return "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200";
    return "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200";
  };

  const getUrgencyIcon = () => {
    if (urgentCount > 0) return "text-red-500";
    return "text-yellow-500";
  };

  return (
    <div className={`rounded-lg border p-4 ${getUrgencyColor()}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ExclamationTriangleIcon className={`h-5 w-5 ${getUrgencyIcon()}`} />
          <div>
            <p className="text-sm font-medium">
              Student payment methods are expiring soon
            </p>
            <p className="text-xs opacity-75">
              Update payment methods to avoid service interruptions
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to="/app/students?filter=expiring-payments"
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
          >
            View Students
          </Link>
          <button
            type="button"
            className="rounded-full p-1 hover:bg-white/20 transition-colors duration-200"
            onClick={handleClose}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}